import React, { useState } from 'react';
import { useAdmin } from '../context/AdminContext';

const PackageCard = ({ package: pkg, onEdit, onDelete, setMessage }) => {
    const { updatePackage, deletePackage } = useAdmin();
    const [updating, setUpdating] = useState(false);
    const [price, setPrice] = useState(pkg.price);

    const handlePriceUpdate = async (e) => {
        e.preventDefault();
        if (price === pkg.price || updating) return;

        setUpdating(true);
        try {
            await updatePackage(pkg._id, { price: parseFloat(price) });
            setMessage('Price updated successfully!');
            // Trigger reload in parent component
            onDelete();
        } catch (error) {
            setMessage('Error updating price: ' + error.message);
            setPrice(pkg.price); // Reset on error
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Are you sure you want to delete "${pkg.name}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await deletePackage(pkg._id);
            setMessage('Package deleted successfully!');
            onDelete(); // Trigger reload in parent component
        } catch (error) {
            setMessage('Error deleting package: ' + error.message);
        }
    };

    // Format price display
    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        }).format(price);
    };

    return (
        <div className="card shadow-sm h-100 border-0 package-card">
            {/* Package Image */}
            <div className="position-relative">
                <img
                    src={pkg.image || '/assets/img/package-placeholder.jpg'}
                    alt={pkg.name}
                    className="card-img-top"
                    style={{
                        height: '200px',
                        objectFit: 'cover',
                        backgroundColor: '#f8f9fa'
                    }}
                    onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300/3B82F6/FFFFFF?text=No+Image+Available';
                    }}
                />
                <div className="position-absolute top-0 end-0 m-2">
                    <span className="badge bg-warning text-dark">
                        <i className="fas fa-star me-1"></i>
                        {pkg.rating || 5}
                    </span>
                </div>
                {pkg.image && pkg.image.includes('cloudinary.com') && (
                    <div className="position-absolute bottom-0 start-0 m-2">
                        <span className="badge bg-info bg-opacity-75 text-white small">
                            <i className="fas fa-cloud me-1"></i>
                            Cloudinary
                        </span>
                    </div>
                )}
            </div>

            {/* Package Details */}
            <div className="card-body d-flex flex-column">
                <h5 className="card-title text-primary fw-bold">{pkg.name}</h5>

                <div className="d-flex justify-content-between text-muted small mb-2">
                    <span>
                        <i className="fas fa-map-marker-alt me-1"></i>
                        {pkg.location}
                    </span>
                    <span>
                        <i className="fas fa-clock me-1"></i>
                        {pkg.duration}
                    </span>
                </div>

                <div className="mb-3">
                    <span className="text-muted small">
                        <i className="fas fa-users me-1"></i>
                        {pkg.persons} {pkg.persons === 1 ? 'person' : 'persons'}
                    </span>
                </div>

                <p className="card-text flex-grow-1 text-muted small">
                    {pkg.description?.length > 100
                        ? `${pkg.description.substring(0, 100)}...`
                        : pkg.description
                    }
                </p>

                {/* Price Section */}
                <div className="mt-auto">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h4 className="text-success mb-0 fw-bold">
                            {formatPrice(pkg.price)}
                        </h4>
                        <small className="text-muted">per person</small>
                    </div>

                    {/* Price Update Form */}
                    <form onSubmit={handlePriceUpdate} className="mb-3">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-light border-end-0">$</span>
                            <input
                                type="number"
                                value={price}
                                onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                                step="1"
                                min="0"
                                className="form-control border-start-0"
                                disabled={updating}
                                style={{ maxWidth: '100px' }}
                            />
                            <button
                                type="submit"
                                disabled={updating || price === pkg.price || price <= 0}
                                className="btn btn-outline-primary btn-sm"
                                title="Update Price"
                            >
                                {updating ? (
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                    <i className="fas fa-sync-alt"></i>
                                )}
                            </button>
                        </div>
                        {price !== pkg.price && price > 0 && (
                            <small className={`d-block mt-1 ${price > pkg.price ? 'text-danger' : 'text-success'}`}>
                                {price > pkg.price ? '↑' : '↓'}
                                {formatPrice(Math.abs(price - pkg.price))}
                            </small>
                        )}
                    </form>

                    {/* Action Buttons */}
                    <div className="d-flex gap-2">
                        <button
                            onClick={() => onEdit(pkg)}
                            className="btn btn-warning btn-sm flex-fill"
                            title="Edit Package Details"
                        >
                            <i className="fas fa-edit me-1"></i>
                            Edit
                        </button>
                        <button
                            onClick={handleDelete}
                            className="btn btn-danger btn-sm flex-fill"
                            title="Delete Package"
                        >
                            <i className="fas fa-trash me-1"></i>
                            Delete
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading Overlay */}
            {updating && (
                <div className="position-absolute top-0 start-0 w-100 h-100 bg-light bg-opacity-75 d-flex align-items-center justify-content-center rounded">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Updating...</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PackageCard;